import { api } from "../../api/client.ts";
import type { Brief, ItemDetail, SpaceOut } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { ATTACHMENTS, attachTarget } from "../../lib/attach.ts";
import { typeName } from "../../lib/documents.ts";
import { upload, uploading } from "../../lib/fileDrop.ts";
import { formatBytes } from "../../lib/format.ts";
import { imageSrc } from "../../lib/itemFiles.ts";
import { spaceApi, spaceUrl } from "../../lib/paths.ts";
import { useAction } from "../../lib/resource.ts";
import { canEdit } from "../../lib/roles.ts";
import { fileNameOf, titleOf } from "../../lib/titles.ts";
import { toast } from "../../lib/toast.ts";
import { Button, Link } from "../../ui/Button.tsx";
import { Dropzone } from "../../ui/Dropzone.tsx";
import { Fold } from "../../ui/Fold.tsx";
import { Section } from "../../ui/misc.tsx";
import { LINK } from "../../ui/styles.ts";
import { NewDocuments } from "./document.tsx";

/** Longest file extension shown on a file's badge. */
const EXT_CHARS = 4;
const EXTENSION = /\.([^./]+)$/u;

/** An image's thumbnail (a document's or spreadsheet's: its first page, its cells, from their top left), or a badge
 *  with the file's extension. */
function Thumb({ b }: { b: Brief }) {
  const src = b.file ? imageSrc(b.file.mime, b.url ?? undefined, b.preview_url ?? undefined) : undefined;
  const shown = src ?? b.thumb_url ?? undefined;
  if (shown) {
    return (
      <img
        src={shown}
        alt=""
        width="40"
        height="40"
        class={`size-10 shrink-0 rounded border border-zinc-200 object-cover ${src ? "" : "bg-white object-left-top"}`}
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      class="flex size-10 shrink-0 items-center justify-center rounded border border-zinc-200 bg-zinc-50 text-[10px] font-semibold text-zinc-500 uppercase"
    >
      {b.title.match(EXTENSION)?.[1]?.slice(0, EXT_CHARS)}
    </span>
  );
}

/** A file: thumbnail or extension, name (to its item), type and size, download; optionally a remove button. */
export function FileRow(p: { space: SpaceOut; b: Brief; busy?: boolean; onRemove?: (() => void) | undefined }) {
  const { b } = p;
  const name = b.hidden ? t("item.privateItem") : titleOf(b);
  return (
    <li class="flex items-center gap-3 py-2">
      <Thumb b={b} />
      <div class="min-w-0 flex-1">
        {b.hidden ? (
          <span class="text-zinc-500 italic">{name}</span>
        ) : (
          <Link href={spaceUrl(p.space, `/items/${b.id}`)} class={`${LINK} block truncate`}>
            {name}
          </Link>
        )}
        {b.file ? (
          <span class="text-xs text-zinc-500">
            {[typeName(b.file.mime, fileNameOf(b)), formatBytes(b.file.size)].join(" · ")}
          </span>
        ) : null}
      </div>
      {b.url ? (
        <a href={b.url} target="_blank" rel="noopener noreferrer" class={`${LINK} text-sm`}>
          {t("attachments.download")}
        </a>
      ) : null}
      {p.onRemove ? (
        <Button
          size="sm"
          variant="ghost"
          disabled={p.busy ?? false}
          aria-label={t("attachments.remove", { title: name })}
          onClick={p.onRemove}
        >
          {t("common.remove")}
        </Button>
      ) : null}
    </li>
  );
}

/** The item's attached files, with drag-and-drop (or pick) to attach more and a remove button each. */
export function Attachments(p: { space: SpaceOut; item: ItemDetail; onChange: (next?: ItemDetail) => void }) {
  const { space, item } = p;
  const files = (item.links[ATTACHMENTS] ?? []).filter((b) => !b.missing);
  const editable = canEdit(space);
  // The page's own target (files dropped anywhere on it): picked or dropped here, they go the same way.
  const drop = attachTarget(space, item, () => p.onChange());
  const attaching = uploading(drop.key);
  const [remove, removing] = useAction(async (b: Brief) => {
    const next = await api.del<ItemDetail>(spaceApi(space, `/items/${item.id}/attachments/${b.id}`));
    toast(t("attachments.removed", { title: titleOf(b) }));
    p.onChange(next);
  });
  if (files.length === 0 && !editable) {
    return null;
  }
  return (
    <Section title={t("attachments.title")}>
      {files.length > 0 ? (
        <div class="mb-3">
          <Fold name={t("attachments.files")} count={files.length}>
            {() => (
              <ul class="divide-y divide-zinc-100">
                {files.map((b) => (
                  <FileRow
                    key={b.id}
                    space={space}
                    b={b}
                    busy={removing}
                    onRemove={editable ? () => remove(b) : undefined}
                  />
                ))}
              </ul>
            )}
          </Fold>
        </div>
      ) : null}
      {editable ? (
        <Dropzone
          compact={true}
          label={attaching ? drop.busy : t("attachments.drop")}
          hint={t("attachments.hint")}
          disabled={attaching}
          onFiles={(list) => upload(drop, list)}
        />
      ) : null}
      {editable ? (
        <div class="mt-2">
          <NewDocuments space={space} attachTo={item.id} />
        </div>
      ) : null}
    </Section>
  );
}
